import React from "react";
import {
  Box,
  Button,
  Skeleton,
  Typography,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type {
  NetworkDashboardMeta,
  NetworkExecutiveSummaryData,
} from "@services/networkDashboard";
import { formatNetworkCurrency, formatNetworkRating } from "@services/networkDashboardFormat";
import {
  ForwardEstimateDriving80InfoTooltip,
  ForwardEstimateHighConfidenceInfoTooltip,
  ForwardEstimateModeledTailInfoTooltip,
} from "../dashboard/dashboardInfoTooltips";
import { DASHBOARD, getDashboardWhiteCardStyles } from "../dashboard/dashboardTheme";

const BAD = "#b85c5c";
const WARN = "#c89234";

function enforcementPillSx(tier: string) {
  if (tier === "severe") return { bgcolor: BAD, color: "#fff" };
  if (tier === "high") return { bgcolor: "#f1d8d8", color: BAD };
  return { bgcolor: "#f5e6c5", color: "#7a5818" };
}

function ManagedBadge() {
  return (
    <Box
      component="span"
      sx={{
        bgcolor: DASHBOARD.accent,
        color: "#fff",
        borderRadius: "4px",
        px: 0.75,
        py: 0.1,
        fontSize: "10px",
        fontWeight: 700,
        lineHeight: 1.4,
      }}
    >
      U
    </Box>
  );
}

function CmsOnlyBadge() {
  return (
    <Box
      component="span"
      sx={{
        color: DASHBOARD.textMuted,
        border: `1px solid ${DASHBOARD.border}`,
        borderRadius: "4px",
        px: 0.75,
        py: 0.1,
        fontSize: "10px",
        lineHeight: 1.4,
      }}
    >
      CMS only
    </Box>
  );
}

export interface NetworkExecutiveSummaryProps {
  data: NetworkExecutiveSummaryData | null;
  meta: NetworkDashboardMeta | null;
  loading: boolean;
  networkLabel?: string;
  onDefineNetwork?: () => void;
  onFacilitySelect?: (ccn: string) => void;
}

export default function NetworkExecutiveSummary({
  data,
  meta,
  loading,
  networkLabel,
  onDefineNetwork,
  onFacilitySelect,
}: NetworkExecutiveSummaryProps) {
  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Skeleton variant="rounded" height={220} />
        <Skeleton variant="rounded" height={80} />
        <Skeleton variant="rounded" height={320} />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography sx={{ color: DASHBOARD.text, mb: 2 }}>
          Define your network to view the executive summary.
        </Typography>
        {onDefineNetwork ? (
          <Button variant="contained" onClick={onDefineNetwork} sx={{ textTransform: "none" }}>
            Define your network
          </Button>
        ) : null}
      </Box>
    );
  }

  const ph = data.penalty_history;
  const fe = data.forward_estimate;
  const glance = data.glance;
  const facilities = data.highest_risk_facilities ?? [];
  const coverage = data.unicomply_coverage;
  const linkedIds = meta?.ccn_facility_ids ?? {};
  const trackedPct =
    coverage.forward_estimate_total > 0
      ? Math.round((coverage.forward_estimate_tracked / coverage.forward_estimate_total) * 100)
      : 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box sx={(theme) => ({ ...getDashboardWhiteCardStyles(theme), p: 3.5 })}>
          <Typography
            sx={{
              fontSize: "10.5px",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: DASHBOARD.textMuted,
              mb: 1.5,
            }}
          >
            3-year penalty history
          </Typography>
          <Typography sx={{ fontSize: 42, fontWeight: 400, color: DASHBOARD.header, lineHeight: 1 }}>
            {formatNetworkCurrency(ph.total_cmp_and_denial_paid)}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.text, mt: 1, mb: 2 }}>
            Paid by <strong>{ph.facilities_with_penalties_count}</strong> of{" "}
            <strong>{ph.facility_count}</strong> facilities in civil monetary penalties and denied
            payment days over the last 36 months. VBP adjustments shown separately below.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${DASHBOARD.borderSubtle}`,
              bgcolor: DASHBOARD.shellBg,
              mb: 2,
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 18, color: DASHBOARD.textMuted }} />
            <Typography sx={{ fontSize: "12.5px", color: DASHBOARD.text }}>
              {ph.trend_callout.text}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 3, pt: 2, borderTop: `1px solid ${DASHBOARD.borderSubtle}` }}>
            <StatBreakdown label="CMP penalties" value={formatNetworkCurrency(ph.breakdown.cmp_penalties)} />
            <StatBreakdown
              label="VBP adjustments"
              value={
                ph.breakdown.vbp_adjustments != null
                  ? formatNetworkCurrency(ph.breakdown.vbp_adjustments)
                  : "—"
              }
            />
            <StatBreakdown
              label="Denied payment days"
              value={formatNetworkCurrency(ph.breakdown.denied_payment_days)}
            />
          </Box>
        </Box>

        <Box
          sx={(theme) => ({
            ...getDashboardWhiteCardStyles(theme),
            p: 3.5,
            bgcolor: "#F0F9FA",
            borderColor: "#b8cdd5",
          })}
        >
          <Typography
            sx={{
              fontSize: "10.5px",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: DASHBOARD.textMuted,
              mb: 1.5,
            }}
          >
            12-month forward estimate
          </Typography>
          <Typography sx={{ fontSize: 42, fontWeight: 400, color: "#7a5818", lineHeight: 1 }}>
            {formatNetworkCurrency(fe.total_12mo)}
          </Typography>
          <Typography sx={{ fontSize: "13px", color: DASHBOARD.text, mt: 1, mb: 2 }}>
            Projected based on current risk profile, enforcement trajectories, and pending survey
            cycles across {glance.facility_count} facilities.
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: 1.5,
              borderRadius: "8px",
              border: "1px solid #e8d5a8",
              bgcolor: "#fdf2e0",
              mb: 2,
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 18, color: WARN }} />
            <Typography sx={{ fontSize: "12.5px", color: DASHBOARD.text }}>
              {fe.trend_callout.text}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 3, pt: 2, borderTop: `1px solid ${DASHBOARD.borderSubtle}` }}>
            <StatBreakdown
              label="High-confidence"
              value={formatNetworkCurrency(fe.breakdown.high_confidence)}
              infoTooltip={<ForwardEstimateHighConfidenceInfoTooltip />}
            />
            <StatBreakdown
              label="Modeled tail"
              value={formatNetworkCurrency(fe.breakdown.modeled_tail)}
              infoTooltip={<ForwardEstimateModeledTailInfoTooltip />}
            />
            <StatBreakdown
              label="Driving 80%"
              value={`${fe.breakdown.facilities_driving_80_pct} fac.`}
              infoTooltip={<ForwardEstimateDriving80InfoTooltip />}
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
          gap: "1px",
          bgcolor: DASHBOARD.border,
          border: `1px solid ${DASHBOARD.border}`,
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        <GlanceCell label="Facilities" value={String(glance.facility_count)} context={`${glance.total_beds} beds`} />
        <GlanceCell
          label="States"
          value={String(glance.states_count)}
          context={glance.states.join(" · ")}
        />
        <GlanceCell
          label="Avg overall ★"
          value={formatNetworkRating(glance.network_avg_overall_rating)}
          context={`National avg ${formatNetworkRating(glance.national_avg_overall_rating)}`}
        />
        <GlanceCell
          label="SFF flagged"
          value={String(glance.sff_flagged_count)}
          context={glance.sff_context ?? undefined}
          valueColor={glance.sff_flagged_count > 0 ? BAD : undefined}
        />
        <GlanceCell
          label="FY24 VBP"
          value={formatNetworkCurrency(glance.fy24_vbp_impact ?? 0)}
          context={
            glance.vbp_facilities_under_1x
              ? `${glance.vbp_facilities_under_1x} under 1.0×`
              : undefined
          }
          valueColor={glance.fy24_vbp_impact != null && glance.fy24_vbp_impact < 0 ? BAD : undefined}
        />
      </Box>

      <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted, fontFamily: "monospace" }}>
        Highest-risk facilities · ranked by 12-month forward penalty estimate · top 5% of network
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "28px 1fr 130px 1fr 100px 24px",
          gap: 2,
          px: 2.5,
          fontSize: "10.5px",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: DASHBOARD.textMuted,
        }}
      >
        <span>#</span>
        <span>Facility</span>
        <span>Enforcement status</span>
        <span>Primary driver</span>
        <span style={{ textAlign: "right" }}>12-mo estimate</span>
        <span />
      </Box>

      <Box sx={{ maxHeight: 396, overflowY: "auto", borderRadius: "12px" }}>
        {facilities.map((row) => {
          const managed = Boolean(linkedIds[row.ccn]);
          return (
            <Box
              key={row.ccn}
              onClick={() => onFacilitySelect?.(row.ccn)}
              sx={{
                display: "grid",
                gridTemplateColumns: "28px 1fr 130px 1fr 100px 24px",
                gap: 2,
                alignItems: "center",
                px: 2.5,
                py: 2,
                mb: 1,
                bgcolor: "#fff",
                border: `1px solid ${DASHBOARD.border}`,
                borderRadius: "12px",
                cursor: onFacilitySelect ? "pointer" : "default",
                "&:hover": onFacilitySelect ? { borderColor: "#b8cdd5" } : undefined,
              }}
            >
              <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: DASHBOARD.textMuted }}>
                {row.rank}
              </Typography>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: DASHBOARD.header }}>
                  {row.provider_name ?? row.ccn}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                  {managed ? <ManagedBadge /> : <CmsOnlyBadge />}
                  <Typography sx={{ fontSize: "11.5px", color: DASHBOARD.textMuted }}>
                    {[row.city, row.state].filter(Boolean).join(", ")}
                  </Typography>
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  ...enforcementPillSx(row.enforcement_tier),
                  display: "inline-flex",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "12px",
                  fontSize: "11.5px",
                  fontWeight: 500,
                  width: "fit-content",
                }}
              >
                {row.enforcement_status}
              </Box>
              <Typography sx={{ fontSize: "12.5px", color: DASHBOARD.text }}>{row.primary_driver}</Typography>
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: 16,
                  fontWeight: 600,
                  color: BAD,
                  textAlign: "right",
                }}
              >
                {formatNetworkCurrency(row.forward_estimate_12mo)}
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 16, color: DASHBOARD.textMuted }} />
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted, fontFamily: "monospace" }}>
        <Box component="span" sx={{ color: DASHBOARD.accent, fontWeight: 600 }}>
          U
        </Box>{" "}
        = Unicomply managed — estimate from Unicomply forward model · CMS only = estimate based on
        CMS citation and enforcement history
      </Typography>

      <Box
        sx={{
          background: "linear-gradient(135deg, #f2f9f8, #eaf4f5)",
          border: "1px solid #b8cdd5",
          borderRadius: "12px",
          p: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: "10.5px",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: DASHBOARD.accent,
            fontWeight: 600,
            mb: 1.5,
          }}
        >
          Unicomply compliance coverage
        </Typography>
        <Typography sx={{ fontSize: 19, color: DASHBOARD.header, mb: 2, lineHeight: 1.5 }}>
          Unicomply actively monitors{" "}
          <strong style={{ color: DASHBOARD.accent }}>{coverage.ftags_monitored_pct}%</strong> of
          the F-tags driving your network&apos;s citation history — and an estimated{" "}
          <strong style={{ color: DASHBOARD.accent }}>
            {formatNetworkCurrency(coverage.forward_estimate_tracked)}
          </strong>{" "}
          of your forward estimate falls in compliance areas we track.
        </Typography>
        <Box sx={{ height: 10, bgcolor: "rgba(255,255,255,0.6)", borderRadius: 6, overflow: "hidden", mb: 1 }}>
          <Box sx={{ width: `${trackedPct}%`, height: "100%", bgcolor: DASHBOARD.accent }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "11.5px", color: DASHBOARD.textMuted, fontFamily: "monospace", mb: 3 }}>
          <span>{formatNetworkCurrency(coverage.forward_estimate_tracked)} tracked</span>
          <span>{formatNetworkCurrency(coverage.forward_estimate_total)} total forward</span>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3 }}>
          <CoverageClaim
            big={`${coverage.ftags_monitored_count} / ${coverage.ftags_total_in_citation_history}`}
            label={`F-tags monitored — including ${coverage.top_tags.slice(0, 3).join(", ")}`}
          />
          <CoverageClaim
            big={formatNetworkCurrency(coverage.forward_estimate_tracked)}
            label="Forward estimate in tracked areas — deficiency-driven CMPs, denied payment days, and VBP adjustments"
          />
          <CoverageClaim
            big={String(coverage.facilities_with_active_tracking ?? 0)}
            label="Facilities with active Unicomply tracking — tracked facilities get deadline alerts before citations occur"
          />
        </Box>
      </Box>

      {networkLabel ? (
        <Typography sx={{ fontSize: "11px", color: DASHBOARD.textMuted, textAlign: "center" }}>
          Data sources: CMS Care Compare · CASPER · Payroll-Based Journal · SNF VBP · {networkLabel}
        </Typography>
      ) : null}
    </Box>
  );
}

function StatBreakdown({
  label,
  value,
  infoTooltip,
}: {
  label: string;
  value: string;
  infoTooltip?: React.ReactNode;
}) {
  return (
    <Box>
      <Typography sx={{ fontFamily: "monospace", fontSize: 14, fontWeight: 500, color: DASHBOARD.header }}>
        {value}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, mt: 0.25 }}>
        <Typography sx={{ fontSize: 11, color: DASHBOARD.textMuted }}>{label}</Typography>
        {infoTooltip}
      </Box>
    </Box>
  );
}

function GlanceCell({
  label,
  value,
  context,
  valueColor,
}: {
  label: string;
  value: string;
  context?: string;
  valueColor?: string;
}) {
  return (
    <Box sx={{ bgcolor: "#fff", p: 2.25 }}>
      <Typography sx={{ fontSize: "10.5px", textTransform: "uppercase", letterSpacing: "0.06em", color: DASHBOARD.textMuted, mb: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, color: valueColor ?? DASHBOARD.header, lineHeight: 1 }}>{value}</Typography>
      {context ? (
        <Typography sx={{ fontSize: "11.5px", color: DASHBOARD.textMuted, mt: 0.5 }}>{context}</Typography>
      ) : null}
    </Box>
  );
}

function CoverageClaim({ big, label }: { big: string; label: string }) {
  return (
    <Box sx={{ borderLeft: `2px solid #b8cdd5`, pl: 2 }}>
      <Typography sx={{ fontSize: 24, fontWeight: 600, color: DASHBOARD.accent }}>{big}</Typography>
      <Typography sx={{ fontSize: 12, color: DASHBOARD.text, mt: 0.75, lineHeight: 1.4 }}>{label}</Typography>
    </Box>
  );
}
